'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DroneAckPayload } from '@/types/coordination';
import {
  flyToOutcomeFromAck,
  isDecisiveFlyToAck,
  manualFlyToMessage,
  type ManualFlyToOutcome,
} from './manualFlyTo';

/**
 * Who owns the `GO-TO` outcome strip, and for exactly how long.
 *
 * `dispatchManualFlyTo` reaches a verdict about a dispatched leg up to six
 * seconds AFTER the operator pressed RELEASE & FLY. Six seconds is a long time
 * on a COP: the operator can have taken the sticks back, selected another
 * aircraft, or confirmed a second leg by then. Two problems follow from that,
 * and both of them are this hook's job.
 *
 * **1. A verdict must not be written into a screen that has moved on.** The
 * strip used to be plain state cleared by an effect on
 * `[selectedDroneId, holdsAuthority]` — an effect that cannot observe a write
 * that happens after it last ran. Confirm a leg, press TAKE MANUAL a second
 * later (the effect runs, against `null`), and at T+6 s the watch writes an
 * outcome nothing will ever clear. Rendering hid it behind the same two
 * conditions, so it sat in state until the operator released manual control —
 * a minute or ten minutes later — and then appeared, a persistent
 * "GO-TO is waiting for ARM on the RC" about a leg long finished.
 *
 * So the context is stamped at DISPATCH ([begin]) and re-read at RESOLUTION
 * ([ManualFlyToLeg.report]), from refs rather than from a closure, and a verdict
 * whose context is gone is dropped instead of stored. The clearing effect stays
 * as well — it is what drops a strip the operator has walked away from — but
 * nothing depends on it winning a race any more.
 *
 * **2. A verdict reached at a deadline is provisional.** `unconfirmed` means
 * "nothing confirmed it within 6 s", and on an armed aircraft the confirming ack
 * waits for a whole KMZ upload (`FlightExecutor.goto` → `pushKMZFileToAircraft`).
 * Over a weak RF link that lands late and the aircraft flies the leg — while the
 * command bar, tracking the same `command_id` for its 75 s lifecycle, renders
 * EXECUTING beside a strip saying it was never confirmed. This hook keeps
 * following that id past the window: a confirming ack CLEARS the strip, and any
 * other decisive ack REPLACES its sentence with what the RC actually said.
 * Nothing here re-sends anything.
 */

/**
 * How long the strip goes on following its command id.
 *
 * `ACK_PENDING_EXPIRE_MS` in `useDroneCommand` — the point at which the command
 * bar itself gives up and stamps the command "No acknowledgement received".
 * Past there the two surfaces agree that nothing answered, so there is nothing
 * left to contradict and nothing left to wait for.
 */
export const MANUAL_FLY_TO_LATE_ACK_WINDOW_MS = 75_000;

/** The strip's content: one dispatched leg, and what is known about it. */
export interface ManualFlyToOutcomeState {
  /** The aircraft the leg was dispatched for — never the one selected now. */
  droneId: string;
  message: string;
  /** True when the RC is holding the leg for an ARM, rather than nothing having answered. */
  held: boolean;
  /** Followed for late acks. Null when the POST produced no id to follow. */
  commandId: string | null;
}

/** Why a verdict was or was not written. The caller decides what to say about it. */
export type ManualFlyToReport =
  /** Written: the operator is looking at this aircraft and does not hold it. */
  | 'recorded'
  /** They have selected another aircraft. The fact is still true; the strip is not theirs to show. */
  | 'stale-selection'
  /** They took the sticks back while the leg was being watched — the sentence's premise is gone. */
  | 'stale-authority'
  /** A newer FLY TO HERE was dispatched; its own verdict is the current one. */
  | 'superseded';

/** One dispatched leg's claim on the strip, stamped when it was dispatched. */
export interface ManualFlyToLeg {
  /** The aircraft this leg was dispatched for, or null if nothing was selected. */
  droneId: string | null;
  /** Report what became of it. Returns whether that reached the strip, and why not. */
  report: (result: {
    message: string;
    outcome: Extract<ManualFlyToOutcome, 'held' | 'unconfirmed'>;
    commandId: string | null;
  }) => ManualFlyToReport;
}

export interface ManualFlyToOutcomeController {
  /** The strip to render, or null. Already scoped to the selected aircraft. */
  outcome: ManualFlyToOutcomeState | null;
  /** Stamp the context at dispatch. Supersedes any leg still being watched. */
  begin: () => ManualFlyToLeg;
  /** DISMISS, and TAKE MANUAL — the strip has been answered. */
  clear: () => void;
}

export function useManualFlyToOutcome({
  selectedDroneId,
  holdsAuthority,
  subscribeDroneAck,
  onLateConfirmed,
  lateWindowMs = MANUAL_FLY_TO_LATE_ACK_WINDOW_MS,
}: {
  selectedDroneId: string | null;
  /** True while THIS console holds manual authority over the selected aircraft. */
  holdsAuthority: boolean;
  subscribeDroneAck: (cb: (ack: DroneAckPayload) => void) => () => void;
  /** The leg was confirmed after all, and the strip has just gone. */
  onLateConfirmed?: () => void;
  /** Injected for tests. */
  lateWindowMs?: number;
}): ManualFlyToOutcomeController {
  const [outcome, setOutcome] = useState<ManualFlyToOutcomeState | null>(null);

  // Read at RESOLUTION time, seconds after the closure that will read them was
  // created. Refs, not closure captures, precisely because the whole defect is
  // that the world moves between dispatch and verdict.
  const selectedDroneIdRef = useRef(selectedDroneId);
  selectedDroneIdRef.current = selectedDroneId;
  const holdsAuthorityRef = useRef(holdsAuthority);
  holdsAuthorityRef.current = holdsAuthority;

  /**
   * Which dispatched leg the strip belongs to.
   *
   * Bumped by [begin], so a second FLY TO HERE confirmed while the first is
   * still being watched invalidates the first: `closeStrip` frees
   * `flyToInFlightRef` as soon as the leg is on the wire, which is what makes a
   * second confirm possible at all, and without this the earlier watch's later
   * resolution would overwrite the newer leg's verdict.
   */
  const generationRef = useRef(0);

  const onLateConfirmedRef = useRef(onLateConfirmed);
  onLateConfirmedRef.current = onLateConfirmed;

  // A strip belongs to the aircraft it was dispatched for AND to a console that
  // is not flying: selecting elsewhere or retaking the sticks drops it rather
  // than parking it to reappear later. Written as a functional update so a
  // verdict landing in the same tick is not raced by it.
  useEffect(() => {
    setOutcome((prev) =>
      prev && prev.droneId === selectedDroneId && !holdsAuthority ? prev : null,
    );
  }, [selectedDroneId, holdsAuthority]);

  const watchedCommandId = outcome?.commandId ?? null;
  // The strip names ONE aircraft, so the acks that may change it are that
  // aircraft's — the same scoping `useDroneCommand` applies before matching a
  // command id, and the reason a leg is never retracted by another airframe.
  const watchedDroneId = outcome?.droneId ?? null;

  /**
   * Keep listening past the 6 s window — the strip is provisional, the command
   * is not finished.
   *
   * A confirming ack clears the strip; any other decisive ack replaces its
   * sentence with what the RC now says (a `held` that arrived late, a rejection
   * with its own reason). The timer only unsubscribes: nothing about the strip
   * changes when the window closes, because "we stopped listening" is not a fact
   * about the aircraft.
   */
  useEffect(() => {
    if (!watchedCommandId) return;
    let unsubscribe: (() => void) | null = null;
    const timer = setTimeout(() => {
      unsubscribe?.();
      unsubscribe = null;
    }, lateWindowMs);

    unsubscribe = subscribeDroneAck((ack) => {
      if (watchedDroneId && ack.drone_id !== watchedDroneId) return;
      if (ack.command_id !== watchedCommandId) return;
      if (!isDecisiveFlyToAck(ack.status)) return;
      const detail = ack.detail ?? null;
      const late = flyToOutcomeFromAck({ status: ack.status ?? null, detail });
      if (late === 'flying') {
        setOutcome((prev) => (prev?.commandId === watchedCommandId ? null : prev));
        onLateConfirmedRef.current?.();
        return;
      }
      // `answered: true` — this branch only runs on a decisive ack, so the
      // "nothing came back in six seconds" wording would be a plain falsehood.
      const message = manualFlyToMessage(late, detail, true);
      if (!message) return;
      setOutcome((prev) =>
        prev?.commandId === watchedCommandId
          ? { ...prev, message, held: late === 'held' }
          : prev,
      );
    });

    return () => {
      clearTimeout(timer);
      unsubscribe?.();
    };
  }, [watchedCommandId, watchedDroneId, lateWindowMs, subscribeDroneAck]);

  const begin = useCallback((): ManualFlyToLeg => {
    const generation = (generationRef.current += 1);
    const droneId = selectedDroneIdRef.current;
    return {
      droneId,
      report: ({ message, outcome: verdict, commandId }) => {
        if (generation !== generationRef.current) return 'superseded';
        if (!droneId || selectedDroneIdRef.current !== droneId) return 'stale-selection';
        if (holdsAuthorityRef.current) return 'stale-authority';
        setOutcome({ droneId, message, held: verdict === 'held', commandId });
        return 'recorded';
      },
    };
  }, []);

  const clear = useCallback(() => setOutcome(null), []);

  return { outcome, begin, clear };
}
